import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { loadEntityData } from '../../../../../shared/utils/loadEntityData';

interface ThemeConfig {
  name: string;
  description: string;
  tagline: string;
  visibility?: {
    products?: boolean;
    upcomingEvents?: boolean;
    events?: boolean;
    features?: boolean;
    contact?: boolean;
  };
  logo: {
    text: string;
    icon?: string;
    image?: string;
    background?: string;
    colors: {
      primary: string;
      accent: string;
    };
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
    warning: string;
    danger: string;
    info: string;
    text: string;
    heading: string;
    background: string;
    surface: string;
    muted: string;
    border: string;
  };
  fonts: {
    heading: string;
    body: string;
    accent?: string;
    mono?: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    full: string;
  };
  shadows: {
    sm: string;
    md: string;
    lg: string;
  };
  gradients: {
    primary: string;
    warm: string;
    cool: string;
  };
  business?: {
    description?: string;
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
    upcomingEvents?: Array<{
      craftFairId: string;
      state?: string;
      city?: string;
      boothNumber?: string;
      note?: string;
      addedAt?: string;
      updatedAt?: string;
    }>;
    social?: {
      [platform: string]: string;
    };
    productCategories?: Array<{
      name: string;
      products: Array<{
        name: string;
        price: string;
        image: string;
      }>;
    }>;
    products?: Array<{
      name: string;
      description: string;
      price?: string;
      icon?: string;
    }>;
    events?: Array<{
      name: string;
      date: string;
      location: string;
      description: string;
    }>;
    features?: Array<{
      title: string;
      description: string;
      icon?: string;
    }>;
  };
}

interface ThemeContextType {
  theme: ThemeConfig | null;
  themeName: string;
  loadTheme: (themeName: string) => Promise<void>;
  refreshTheme: (themeName: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: string;
}

// Default theme values - defined outside component to prevent re-creation
const DEFAULT_THEME_VALUES: Partial<ThemeConfig> = {
  fonts: {
    heading: "'Inter', sans-serif",
    body: "'Inter', sans-serif"
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    full: '9999px'
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
  },
  gradients: {
    primary: 'linear-gradient(135deg, #EC4899, #DB2777)',
    warm: 'linear-gradient(135deg, #F59E0B, #D97706)',
    cool: 'linear-gradient(135deg, #3B82F6, #2563EB)'
  },
  colors: {
    primary: '#EC4899',
    secondary: '#DB2777',
    accent: '#F472B6',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6',
    text: '#1F2937',
    heading: '#111827',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    muted: '#6B7280',
    border: '#E5E7EB'
  }
};

// Build the fully-merged ThemeConfig from an entity payload ({ theme, business }) —
// the SAME shape loadEntityData returns and the SSR layer injects. PURE (no DOM/window),
// so it runs identically on the server (SSR), as the client's first render (from injected
// data), and inside loadTheme. This is what makes static === dynamic.
export function buildMergedTheme(entityData: any, name?: string): ThemeConfig {
  const logoColors = entityData?.business?.logo?.colors || {};
  const derivedColors = logoColors.primary ? {
    primary: logoColors.primary,
    secondary: logoColors.primary,
    accent: logoColors.accent || logoColors.primary
  } : {};

  const loadedTheme: any = {
    ...(entityData?.theme || {}),
    colors: derivedColors,
    business: entityData?.business,
    logo: entityData?.business?.logo,
    name: entityData?.business?.name || entityData?.name || name,
    description: entityData?.business?.description,
    tagline: entityData?.business?.tagline,
  };

  return {
    ...DEFAULT_THEME_VALUES,
    ...loadedTheme,
    colors: { ...DEFAULT_THEME_VALUES.colors, ...loadedTheme.colors },
    fonts: { ...DEFAULT_THEME_VALUES.fonts, ...loadedTheme.fonts },
    spacing: { ...DEFAULT_THEME_VALUES.spacing, ...loadedTheme.spacing },
    borderRadius: { ...DEFAULT_THEME_VALUES.borderRadius, ...loadedTheme.borderRadius },
    shadows: { ...DEFAULT_THEME_VALUES.shadows, ...loadedTheme.shadows },
    gradients: { ...DEFAULT_THEME_VALUES.gradients, ...loadedTheme.gradients },
    logo: loadedTheme.logo
  } as ThemeConfig;
}

// Read server-injected initial data (set by the SSR shell as window.__INITIAL_DATA__).
// Returns null on the server or when nothing was injected.
function getInjectedInitialData(): any | null {
  if (typeof window === 'undefined') return null;
  return (window as any).__INITIAL_DATA__ || null;
}

export const ThemeProvider: React.FC<ThemeProviderProps & { initialData?: any }> = ({
  children,
  defaultTheme,
  initialData
}) => {
  // Seed the theme synchronously from injected data (SSR prop or window.__INITIAL_DATA__)
  // so the FIRST render — server and client — already has the real theme (no empty flash,
  // no fetch needed). Falls back to the normal async loadTheme path when nothing is injected.
  const seed = initialData || getInjectedInitialData();
  const [theme, setTheme] = useState<ThemeConfig | null>(
    seed ? buildMergedTheme(seed, defaultTheme) : null
  );
  const [themeName, setThemeName] = useState<string>(defaultTheme || seed?.businessId || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadTheme = useCallback(async (name: string) => {
    setIsLoading(true);
    setError(null);

    try {
      let mergedTheme: ThemeConfig;

      // Load entity data using platform-wide utility (also stores in localStorage),
      // then build the theme with the SAME pure function the SSR layer uses.
      try {
        const entityData = await loadEntityData('businesses', name);
        mergedTheme = buildMergedTheme(entityData, name);
        console.log('[ThemeContext] Loaded entity data:', name);
      } catch (dataErr) {
        console.log('[ThemeContext] Failed to load entity data:', dataErr);
        mergedTheme = buildMergedTheme({ business: { name } }, name);
      }

      setTheme(mergedTheme);
      setThemeName(name);

      // Apply CSS custom properties
      applyThemeToDOM(mergedTheme);

    } catch (err) {
      setError(`Failed to load theme: ${err}`);
      console.error('Theme loading error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Silent refresh without loading state - for live updates from localStorage
  const refreshTheme = useCallback(async (name: string) => {
    if (typeof window === 'undefined') return; // client-only (reads localStorage)
    try {
      // Read theme and data from localStorage (where the agents just saved updated info)
      const themeStorageKey = `businesses_theme_${name}`;
      const dataStorageKey = `businesses_data_${name}`;

      const cachedTheme = localStorage.getItem(themeStorageKey);
      const cachedData = localStorage.getItem(dataStorageKey);

      if (!cachedTheme && !cachedData) {
        console.warn('[ThemeContext] No cached data found for refresh, skipping');
        return;
      }

      const themeData = cachedTheme ? JSON.parse(cachedTheme) : {};
      const businessData = cachedData ? JSON.parse(cachedData) : {};

      // Merge theme styling with business content
      const loadedTheme = {
        ...themeData,
        ...businessData.business,
        name: businessData.business?.name || name,
        description: businessData.business?.description || `${name} business`,
        tagline: businessData.business?.tagline || '',
        logo: {
          ...themeData.logo,
          ...Object.fromEntries(
            Object.entries(businessData.business?.logo || {}).filter(([, v]) => v !== '' && v != null)
          )
        },
        business: businessData.business
      };

      // Merge with defaults
      const mergedTheme: ThemeConfig = {
        ...DEFAULT_THEME_VALUES,
        ...loadedTheme,
        colors: { ...DEFAULT_THEME_VALUES.colors, ...themeData.colors },
        fonts: { ...DEFAULT_THEME_VALUES.fonts, ...themeData.fonts },
        spacing: { ...DEFAULT_THEME_VALUES.spacing, ...themeData.spacing },
        borderRadius: { ...DEFAULT_THEME_VALUES.borderRadius, ...themeData.borderRadius },
        shadows: { ...DEFAULT_THEME_VALUES.shadows, ...themeData.shadows },
        gradients: { ...DEFAULT_THEME_VALUES.gradients, ...themeData.gradients },
        logo: loadedTheme.logo
      } as ThemeConfig;

      console.log('[ThemeContext] Refreshed theme from localStorage');
      console.log('[ThemeContext] mergedTheme.fonts:', mergedTheme.fonts);
      console.log('[ThemeContext] mergedTheme.colors.primary:', mergedTheme.colors.primary);
      setTheme(mergedTheme);
      applyThemeToDOM(mergedTheme);
    } catch (err) {
      console.error('Theme refresh error:', err);
    }
  }, []);

  const applyThemeToDOM = (themeConfig: ThemeConfig) => {
    if (typeof document === 'undefined') return; // no-op on the server (SSR)
    const root = document.documentElement;

    // Apply color variables
    if (themeConfig.colors) {
      Object.entries(themeConfig.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
    }

    // Apply font variables
    if (themeConfig.fonts) {
      Object.entries(themeConfig.fonts).forEach(([key, value]) => {
        root.style.setProperty(`--font-${key}`, value);
      });
    }

    // Apply spacing variables
    if (themeConfig.spacing) {
      Object.entries(themeConfig.spacing).forEach(([key, value]) => {
        root.style.setProperty(`--spacing-${key}`, value);
      });
    }

    // Apply border radius variables
    if (themeConfig.borderRadius) {
      Object.entries(themeConfig.borderRadius).forEach(([key, value]) => {
        root.style.setProperty(`--radius-${key}`, value);
      });
    }

    // Apply shadow variables
    if (themeConfig.shadows) {
      Object.entries(themeConfig.shadows).forEach(([key, value]) => {
        root.style.setProperty(`--shadow-${key}`, value);
      });
    }

    // Apply gradient variables
    if (themeConfig.gradients) {
      Object.entries(themeConfig.gradients).forEach(([key, value]) => {
        root.style.setProperty(`--gradient-${key}`, value);
      });
    }
  };

  useEffect(() => {
    if (defaultTheme) {
      loadTheme(defaultTheme);
    }
  }, [defaultTheme]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: ThemeContextType = {
    theme,
    themeName,
    loadTheme,
    refreshTheme,
    isLoading,
    error
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};